import * as React from 'react'

import { type SelectOption } from '@factorialco/deprecated-design-system'

import { useAsyncData } from 'modules/core/hooks/useAsyncData'
import { cotizationGroups } from 'modules/core/lib/cotizationGroups'
import countryList from 'modules/core/lib/countryList'
import i18n from 'modules/core/lib/i18n'
import legalEntities from 'modules/core/store/collections/legalEntities'

import { TriggerAttributes } from 'modules/automations/lib/triggerAttributes'
import { ContractTypes as FrContractTypes } from 'modules/contracts/store/collections/france/contractTypes'
import { ProfessionalCategories as FrProfessionalCategories } from 'modules/contracts/store/collections/france/professionalCategories'
import { WorkTypes } from 'modules/contracts/store/collections/france/workTypes'
import { ContractTypes as DeContractTypes } from 'modules/contracts/store/collections/germany/contractTypes'
import type { IPayrollLegalEntityResource } from 'modules/contracts/store/collections/payrollLegalEntityResource'
import { ContractTypes as PtContractTypes } from 'modules/contracts/store/collections/portugal/contractTypes'
import {
  ContractTypes,
  IContractType,
} from 'modules/contracts/store/collections/spain/contractTypes'
import { EducationLevels } from 'modules/contracts/store/collections/spain/educationLevels'
import { ProfessionalCategories } from 'modules/contracts/store/collections/spain/professionalCategories'
import { WorkingDayTypes } from 'modules/contracts/store/collections/spain/workingDayTypes'
import ContractVersion from 'modules/contracts/store/models/ContractVersion'
import type { IWorkingDayType } from 'modules/contracts/store/models/WorkingDayType'
import getTitle from 'modules/documents/components/Documents/getTitle'
import folders from 'modules/documents/store/collections/folders'
import { ALL_TYPES } from 'modules/documents/store/models/Document'
import Review from 'modules/documents/store/models/Review'
import jobRolesCollection from 'modules/job_catalog/store/collections/roles'
import type { Attributes as JobRoleAttributes } from 'modules/job_catalog/store/models/JobRole'
import locations from 'modules/locations/store/collections/locations'
import teams from 'modules/teams/store/collections/teams'
import leaveTypes from 'modules/timeoff/store/collections/leaveTypes'
import type LeaveType from 'modules/timeoff/store/models/LeaveType'

export const reviewStatusOptions = (): SelectOption[] => {
  return [
    {
      label: i18n.t('reviews.status.approved'),
      value: Review.Status.approved,
    },
    {
      label: i18n.t('reviews.status.rejected'),
      value: Review.Status.rejected,
    },
  ]
}

export const countryOptions = (): SelectOption[] => {
  return countryList[i18n.locale as keyof typeof countryList] || countryList.en
}

export const booleanOptions = (attr: string): SelectOption[] => {
  const falseLabel =
    attr === TriggerAttributes.document_public ? i18n.t('common.private') : i18n.t('common.false')

  const trueLabel =
    attr === TriggerAttributes.document_public ? i18n.t('common.public') : i18n.t('common.true')

  return [
    {
      label: trueLabel,
      value: true,
    },
    {
      label: falseLabel,
      value: false,
    },
  ]
}

export const genderOptions = (): SelectOption[] => {
  return [
    {
      label: i18n.t('common.female'),
      value: 'female',
    },
    {
      label: i18n.t('common.male'),
      value: 'male',
    },
  ]
}

export function LeaveTypeOptions(): SelectOption[] {
  useAsyncData(() => leaveTypes.fetch(), [])

  return leaveTypes.map((leaveType: LeaveType) => ({
    value: leaveType.id,
    label: leaveType.name,
  }))
}

export function LeaveTypeDefaultOptions(): SelectOption[] {
  useAsyncData(() => leaveTypes.fetch(), [])

  return leaveTypes.map((leaveType: LeaveType) => ({
    value: leaveType.attributes.identifier,
    label: leaveType.name,
  }))
}

export function LocationOptions(): SelectOption[] {
  useAsyncData(() => locations.fetch(), [])

  return locations.map((location) => ({
    value: location.attributes.id,
    label: location.attributes.name,
  }))
}

export function TeamOptions(): SelectOption[] {
  useAsyncData(() => teams.fetch(), [])

  return teams.map((team) => ({
    value: team.attributes.id,
    label: team.attributes.name,
  }))
}

export function DocumentFolderOptions(): SelectOption[] {
  useAsyncData(() => folders.fetch({ data: { active: true } }), [])

  return folders.map((folder) => ({
    value: folder.attributes.id,
    label: folder.attributes.name,
  }))
}

export function documentTypeOptions(): SelectOption[] {
  return Object.keys(ALL_TYPES).map((type) => ({
    value: ALL_TYPES[type as keyof typeof ALL_TYPES],
    label: getTitle(type),
  }))
}

export const employeeActiveOptions = () => {
  return [
    {
      label: i18n.t('automations.options.employees.status.active'),
      value: 'true',
    },
    {
      label: i18n.t('automations.options.employees.status.terminated'),
      value: 'false',
    },
  ]
}

interface CategoriesType {
  id: string
  legal_entity_id: string
  name: string
  default: boolean
  archived: boolean
}

// For backward compatibility
export const SpainProfessionalCategories = (): SelectOption[] => {
  const [categories, setCategories] = React.useState<SelectOption[]>([])
  useAsyncData(() => {
    return new ProfessionalCategories().fetch<CategoriesType[]>().then((categories) => {
      const allProfessionalCategories = categories.map((category) => {
        const legalEntity = legalEntities.find({ id: category.legal_entity_id })

        return {
          value: category.id,
          label: `[${legalEntity?.name}] ${category.name}`,
        }
      })

      setCategories(allProfessionalCategories)
    })
  }, [])

  return categories
}

export const AllProfessionalCategories = (): SelectOption[] => {
  const [categories, setCategories] = React.useState<SelectOption[]>([])
  useAsyncData(async () => {
    const [esProfessionalCategories, frProfessionalCategories] = await Promise.all([
      new ProfessionalCategories().fetch(),
      new FrProfessionalCategories().fetch(),
    ])

    const allProfessionalCategories = [
      ...esProfessionalCategories,
      ...frProfessionalCategories,
    ].map((category) => {
      const legalEntity = legalEntities.find({ id: category.legal_entity_id })

      return {
        value: `${legalEntity?.country}_${category.id}`,
        label: `[${legalEntity?.name}] ${category.name}`,
      }
    })

    setCategories(allProfessionalCategories)
  }, [])

  return categories
}

export const WorkingDayTypesOptions = (): SelectOption[] => {
  const [categories, setCategories] = React.useState<SelectOption[]>([])

  useAsyncData(async () => {
    const [esWorkingDayTypes, deWorkingDayTypes] = await Promise.all([
      new WorkingDayTypes().fetch(),
      new WorkTypes().fetch(),
    ])
    const workingDayTypesOptions = [...esWorkingDayTypes, ...deWorkingDayTypes].map(
      (option: IWorkingDayType) => {
        const legalEntity = legalEntities.find({ id: option.legal_entity_id })

        return {
          value: `${legalEntity?.country}_${option.id}`,
          label: `[${legalEntity?.name}] ${option.name}`,
        }
      }
    )

    setCategories(workingDayTypesOptions)
  }, [])

  return categories
}

export const ContractTypesOptions = (): SelectOption[] => {
  const [categories, setCategories] = React.useState<SelectOption[]>([])

  useAsyncData(async () => {
    const [esContractTypes, deContractTypes, frContractTypes, ptContractTypes] = await Promise.all([
      new ContractTypes().fetch(),
      new DeContractTypes().fetch(),
      new FrContractTypes().fetch(),
      new PtContractTypes().fetch(),
    ])

    const contractTypesOptions = [
      ...esContractTypes,
      ...deContractTypes,
      ...frContractTypes,
      ...ptContractTypes,
    ].map((option: IContractType) => {
      const legalEntity = legalEntities.find({ id: option.legal_entity_id })

      return {
        value: `${legalEntity?.country}_${option.id}`,
        label: `[${legalEntity?.name}] ${option.name}`,
      }
    })

    setCategories(contractTypesOptions)
  }, [])

  return categories
}

export const EmployeeEducationLevelOptions = (): SelectOption[] => {
  const [categories, setCategories] = React.useState<SelectOption[]>([])

  useAsyncData(() => {
    return new EducationLevels().fetch().then((levels) => {
      const educationLevelOptions = levels.map((level: IPayrollLegalEntityResource) => {
        const legalEntity = legalEntities.find({ id: level.legal_entity_id })

        return {
          value: `${legalEntity?.country}_${level.id}`,
          label: `[${legalEntity?.name}] ${level.name}`,
        }
      })

      setCategories(educationLevelOptions)
    })
  }, [])

  return categories
}

export const JobCatalogRoles = (): SelectOption[] => {
  const [roles, setRoles] = React.useState<SelectOption[]>([])

  useAsyncData(() => {
    return jobRolesCollection.fetch().then((roles) => {
      const jobRolesOptions = roles.map((job: JobRoleAttributes) => {
        return {
          value: job.id,
          label: job.name,
        }
      })

      setRoles(jobRolesOptions)
    })
  }, [])

  return roles
}

export const CotizationGroupOptions = (): SelectOption[] => {
  return cotizationGroups()
}

export const SalaryFrequencyOptions = (): SelectOption[] => {
  return Object.keys(ContractVersion.SalaryFrequencies()).map((item) => {
    return { value: item, label: item }
  })
}

export const workingHoursFrequencyOptions = (): SelectOption[] => {
  return [
    {
      label: i18n.t('automations.options.working_hours_frequency.day'),
      value: 'day',
    },
    {
      label: i18n.t('automations.options.working_hours_frequency.week'),
      value: 'week',
    },
    {
      label: i18n.t('automations.options.working_hours_frequency.month'),
      value: 'month',
    },
    {
      label: i18n.t('automations.options.working_hours_frequency.year'),
      value: 'year',
    },
  ]
}
